import React from 'react';
import Link from 'next/link';
import { twJoin } from 'tailwind-merge';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { getOptionTileStateClasses } from '@/ui/components/shared/option-tile/option-tile-styles';
import { ProductAlternativeModel } from '@/ui/components/pages/ProductDetail/types/ProductAlternativeModel';

export interface ProductHighlightTabProps {
  product: ProductAlternativeModel;
  isSelected: boolean;
}

export function ProductHighlightTab({ product, isSelected }: Readonly<ProductHighlightTabProps>) {
  const { localizedLink } = useLocaleLink();
  const { formatCurrency, formatSubscriptionPrice } = useCurrencyFormatter();
  const productImage = product.image;
  const productPath = product.path;

  const tabContent = (
    <div
      data-testid="product-highlight-tab"
      className={twJoin('rounded-lg p-4', getOptionTileStateClasses(isSelected))}
    >
      <div className="flex space-x-4">
        <div className="relative z-0 h-16 w-16 flex-shrink-0 rounded-sm border border-neutral-5">
          <div className="absolute bottom-0 left-0 right-0 -z-10 h-2/4 bg-gradient-to-t from-neutral-5 to-transparent opacity-30" />
          <OptimizedImage
            image={productImage}
            fill
            className="object-contain py-1"
            priority
            sizes="100px"
          />
        </div>
        <div className="w-full">
          <div className="flex justify-between gap-4 text-lg font-bold">
            <p>{product.name}</p>
            <p className="my-px">
              {product.subscriptionPlan?.periods[0]?.recurring
                ? formatSubscriptionPrice(product.subscriptionPlan.periods[0].recurring)
                : formatCurrency(product.priceVariant)}
            </p>
          </div>
          <RichTextRenderer className="mt-1 text-gray-700" blocks={product.description} />
        </div>
      </div>
    </div>
  );

  return productPath ? (
    <Link href={localizedLink(productPath, true)} className="block" scroll={false} prefetch replace>
      {tabContent}
    </Link>
  ) : (
    <div className="block">{tabContent}</div>
  );
}
